/**
 * problemBodyV2 — 문제 본문(조판)을 **묶어** 받는 이음매.
 *
 * 부르는 곳(2026-08-28): PaperComposeModal 의 **가운데 '담은 문항' 목록** 하나다.
 * 담은 문항은 검색에 안 뜬 것일 수도 있어(서버가 준 시험지를 그대로 연 경우) 조회가 따로 필요하다.
 *
 * **검색 결과 카드는 이것을 부르지 않는다.** 그쪽 미리보기는 검색 호출이 직접 받는다 —
 * searchForCompose 가 `with_body=1` 을 얹고 응답 item 의 `body_lexical` 을 그 자리에서 지도로
 * 만든다(toSearchBodyMap). 계약 §9 가 설계한 「별도 왕복 없음」이 그 길이다.
 * 그전에는 검색을 부른 뒤 **같은 엔드포인트를 problem_ids 로 한 번 더** 불렀다 — 이 이음매의
 * 시그니처를 지키려던 것인데, 페이지를 넘길 때마다 왕복이 1회씩 더 나서 백엔드가 A안을 고른
 * 이유를 정면으로 잃었다. 그래서 검색 쪽 호출을 걷어내고 검색 호출에 스위치를 얹었다.
 * 그러니 **검색이 이미 들고 온 id 를 여기로 다시 묻지 마라** — 부르는 쪽이 searchBodies 에
 * 있는지 먼저 보고 없는 것만 모아 넘긴다.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  계약 §9 — 검색에 본문을 동봉하는 스위치 (2026-08-26 배포)
 * ══════════════════════════════════════════════════════════════════════════
 *   GET /api/v2/exam-papers/problem-search?problem_ids=…&with_body=1
 *     → 각 item 에 `body_lexical` (GET /api/v2/problems/{id} 와 같은 형태).
 *   본문이 없는 문항은 `body_lexical:null` — 그 카드만 평문 `preview` 로 남고 목록 전체는
 *   실패하지 않는다. 스위치를 안 주면 종전과 같은 응답이다(응답 크기 유지).
 *
 * 이것이 요청서(FE-20260826-v2-mathcept-api.md §2-4)의 A안이다. 이전에 적어 둔 두 갈래
 * — ① v2 단건 × 20 왕복 ② v1 /api/problems/print/page — 는 **둘 다 필요 없어졌다.**
 *
 * 돌려주는 형태: `{ [problem_id]: editorState }`
 *   - `editorState` 는 문자열(JSON)이든 객체든 된다 — 부르는 쪽이 둘 다 흡수한다.
 *   - 본문이 없는 id 는 **키를 넣지 않는다.** 그 카드는 지금처럼 평문 요약으로 남는다.
 *   - 실패는 던지지 않고 null 을 돌려준다 — 화면이 평문 요약으로 버틴다.
 *
 * 부르는 쪽이 이 값을 받아 Lexical → HTML 로 굳힌다(변환기는 PaperComposeModal 에 이미 있다).
 * 그러니 여기서는 **받은 그대로** 돌려주면 되고, 변환·캐시를 신경 쓸 필요가 없다.
 *
 * v1 을 여기에 끌어오지 마라 — 이 이음매는 v2 전용 자리를 위한 것이고,
 * v1 을 넣는 순간 그 화면이 v2 전용이 아니게 된다(오너 방침 2026-08-26).
 * ══════════════════════════════════════════════════════════════════════════
 */
import { fetchWithTokenRefresh } from '../../utils/apiUtils';

/**
 * 한 번에 묻는 id 수. 검색 한 페이지는 20건이지만 결과가 커져도 잘리지 않게 100 에서 끊고
 * 덩이를 병렬로 부른다. `per_page` 는 **반드시 함께 보낸다** — 기본값이 20 이라(계약 §1-5)
 * 안 보내면 21번째부터 조용히 사라지고, 그 카드들만 평문 요약으로 남아 원인이 안 보인다.
 */
const CHUNK = 100;

/** 검색 응답의 배열 자리 — PaperComposeModal 의 searchForCompose 와 같은 순서로 흡수한다 */
function toItems(d) {
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.list)) return d.list;
  return [];
}

/** 덩이 하나 — 실패하면 던진다(부르는 쪽이 덩이별로 받아 낸다) */
async function fetchChunk(ids, getAccessToken, withBody) {
  const params = new URLSearchParams({
    problem_ids: ids.join(','),
    page: '1',
    per_page: String(ids.length),
  });
  // 본문은 **부를 때 고른다.** 통계를 내려고 전 문항의 개념·난이도를 거둘 때는 본문이
  // 필요 없고, 그때 with_body 를 얹으면 200문항짜리가 본문 200벌을 한꺼번에 끌고 온다.
  if (withBody) params.append('with_body', '1');
  const res = await fetchWithTokenRefresh(`/api/v2/exam-papers/problem-search?${params}`, {}, getAccessToken);
  // 401 이면 공용 헬퍼가 /signin 으로 보내며 null 을 준다 — 그 경우도 여기서는 그냥 실패다
  if (!res || res.status !== 'success') throw new Error(res?.message || '문제 본문을 불러오지 못했습니다.');
  return toItems(res.data);
}

/**
 * 문항 **항목 그대로** — `{ [String(problem_id)]: item }`.
 *
 * 본문 지도(fetchProblemBodiesV2)가 body_lexical 하나만 뽑아 주는 것과 달리, 검색 응답 item 을
 * 통째로 돌려준다. 좌측 통계가 개념·난이도·라벨·need_* 를 함께 세야 하기 때문이다 —
 * 같은 응답에 이미 실려 오는 값이라 이걸로 거두면 **왕복이 늘지 않는다.**
 *
 * `withBody:false`(기본)가 요점이다. 통계는 전 문항이 필요하고 본문은 눈에 든 것만 필요해서,
 * 이 함수 하나를 두 트랙이 다른 스위치로 나눠 쓴다.
 *
 * 돌려주는 것
 *   - 성공: 지도. **응답에 없는 id 는 키가 없다** — 부르는 쪽이 '못 받음'으로 셈한다
 *     (삭제됐거나 status≠999 인 문항이 그렇다).
 *   - 덩이가 하나라도 성공하면 그 몫은 살린다(allSettled).
 *   - **전부 실패하면 null** — 던지지 않는다. 화면이 통계 없이도 서야 한다.
 *   - 빈손이면 `{}`(왕복하지 않는다 — problem_ids 를 비워 보내면 필터 없는 전체가 온다).
 */
export async function fetchProblemItemsV2(problemIds, getAccessToken, { withBody = false } = {}) {
  const ids = (Array.isArray(problemIds) ? problemIds : [])
    .map((v) => String(v ?? '').trim())
    .filter((s) => s !== '');
  if (ids.length === 0) return {};
  const uniq = Array.from(new Set(ids)); // 같은 id 가 두 번 들어와도 한 번만 묻는다

  const chunks = [];
  for (let i = 0; i < uniq.length; i += CHUNK) chunks.push(uniq.slice(i, i + CHUNK));

  // allSettled 로 덩이를 가둔다 — 한 덩이가 넘어져도 나머지는 살아남는다
  // (Promise.all 이면 100건 중 하나의 실패가 전부를 되돌린다).
  const settled = await Promise.allSettled(chunks.map((c) => fetchChunk(c, getAccessToken, withBody)));
  const ok = settled.filter((r) => r.status === 'fulfilled');
  if (ok.length === 0) {
    console.error('v2 문항 묶음 로드 실패:', settled[0]?.reason);
    return null;
  }

  const out = {};
  ok.forEach((r) => {
    r.value.forEach((it) => {
      const pid = it?.problem_id ?? it?.id;
      if (pid != null) out[String(pid)] = it;
    });
  });
  return out;
}

/**
 * 본문만 뽑은 지도 — 위 함수의 얇은 껍데기다. **돌려주는 규약은 종전 그대로**:
 * 본문이 없는 문항(body_lexical:null)은 **키를 넣지 않는다**(그 카드가 평문 preview 로 폴백),
 * 전부 실패하면 null, 빈손이면 `{}`.
 */
export async function fetchProblemBodiesV2(problemIds, getAccessToken) {
  const items = await fetchProblemItemsV2(problemIds, getAccessToken, { withBody: true });
  if (items == null) return null;
  const out = {};
  Object.entries(items).forEach(([key, it]) => {
    if (it?.body_lexical != null) out[key] = it.body_lexical;
  });
  return out;
}

export default fetchProblemBodiesV2;
